import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Lifebuoy, Warning, CheckCircle, ArrowRight, ClockCounterClockwise, FlowArrow,
  UsersThree, Plus, ChatCircleDots,
} from "@phosphor-icons/react";

function fmtDays(n) {
  if (n === null || n === undefined) return "—";
  if (n < 1) return "today";
  if (n === 1) return "1 day";
  return `${n} days`;
}

function buildNudgePrefill(lead) {
  const name = (lead?.name || "this lead").trim();
  const companyPart = lead?.company ? ` at ${lead.company}` : "";
  const stagePart = lead?.stage_name ? `in the "${lead.stage_name}" stage` : "in an open stage";
  const days = lead?.days_since_update;
  const daysPart = (days === null || days === undefined)
    ? "with no recent activity"
    : `for ${days} day${days === 1 ? "" : "s"} with no activity`;
  return (
    `Draft a re-engagement email for ${name}${companyPart} — they've been ${stagePart} ${daysPart}. ` +
    `Keep it warm, specific, and offer a concrete next step. ` +
    `Use our Knowledge Base for tone/context if relevant.`
  );
}

const fmtValue = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString());

function FollowUpSection({ testid, title, rows, empty, showOverdue }) {
  return (
    <section data-testid={testid} className="rounded-md border border-white/10 bg-graphite-900">
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between gap-3">
        <h2 className="font-display text-lg tracking-tight">{title}</h2>
        <div className="text-[11px] text-ink-secondary tabular-nums" data-testid={`${testid}-count`}>
          {rows.length} lead{rows.length === 1 ? "" : "s"}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-ink-secondary" data-testid={`${testid}-empty`}>{empty}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`${testid}-table`}>
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                <th className="px-5 py-2.5 font-medium">Lead / Company</th>
                <th className="px-5 py-2.5 font-medium">Stage</th>
                <th className="px-5 py-2.5 font-medium">Next action</th>
                <th className="px-5 py-2.5 font-medium">Due date</th>
                <th className="px-5 py-2.5 font-medium text-right">Opportunity value</th>
                {showOverdue && <th className="px-5 py-2.5 font-medium text-right">Overdue days</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((l) => (
                <tr key={l.lead_id} data-testid={`${testid}-row-${l.lead_id}`} className="hover:bg-white/[0.02]">
                  <td className="px-5 py-2.5 text-ink-primary">
                    {l.name || "(unnamed)"}
                    {l.company && <div className="text-[11px] text-ink-secondary">{l.company}</div>}
                  </td>
                  <td className="px-5 py-2.5 text-ink-secondary">{l.stage_name || "—"}</td>
                  <td className="px-5 py-2.5 text-ink-secondary">{l.next_action || "—"}</td>
                  <td className="px-5 py-2.5 text-ink-secondary tabular-nums">{l.follow_up_date}</td>
                  <td className="px-5 py-2.5 text-right text-ink-secondary tabular-nums">{fmtValue(l.opportunity_value)}</td>
                  {showOverdue && (
                    <td className="px-5 py-2.5 text-right text-amber-200/90 tabular-nums" data-testid={`${testid}-days-${l.lead_id}`}>{l.days_overdue}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function LeadRecovery() {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [recovery, setRecovery] = useState(null);
  const [nurture, setNurture] = useState(null);
  const [loading, setLoading] = useState(true);

  const [enrollLead, setEnrollLead] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [chosenSeqId, setChosenSeqId] = useState("");
  const [enrolling, setEnrolling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, n] = await Promise.all([
        api.get("/leads/recovery").catch(() => ({ data: null })),
        api.get("/dashboard/nurture-due-today").catch(() => ({ data: [] })),
      ]);
      setRecovery(r.data);
      setNurture((n.data || []).filter((x) => x.due_status === "overdue"));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const openEnroll = async (lead) => {
    setEnrollLead(lead);
    setChosenSeqId("");
    try {
      const { data } = await api.get("/nurture-sequences");
      setSequences((data || []).filter((s) => s.is_active !== false));
    } catch { setSequences([]); }
  };

  const openNudge = (lead) => {
    const prefill = buildNudgePrefill(lead);
    navigate(`/assistant?prefill=${encodeURIComponent(prefill)}`);
  };

  const doEnroll = async () => {
    if (!enrollLead || !chosenSeqId) return;
    setEnrolling(true);
    try {
      await api.post(`/leads/${enrollLead.lead_id}/enroll`, { sequence_id: chosenSeqId });
      toast.success(`Enrolled ${enrollLead.name} in sequence`);
      setEnrollLead(null);
      setChosenSeqId("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not enroll");
    } finally { setEnrolling(false); }
  };

  const stale = recovery?.needs_attention || [];
  const threshold = recovery?.stale_threshold_days || 14;
  const overdue = nurture || [];
  const dueToday = recovery?.due_today || [];
  const overdueFollowUps = recovery?.overdue_follow_ups || [];
  const nothing = !loading && stale.length === 0 && overdue.length === 0
    && dueToday.length === 0 && overdueFollowUps.length === 0;

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="recovery-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary flex items-center gap-1.5">
            <Lifebuoy weight="duotone" className="h-3 w-3" /> Leads · Recovery
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">
            Bring leads back on track
          </h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-2xl">
            Leads currently in an open pipeline stage that haven't been touched in a while,
            plus nurture-sequence steps that are overdue. Reuses your existing data — no separate tracking.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-ink-secondary">Loading recovery view…</div>
      ) : nothing ? (
        <div
          className="mt-10 rounded-md border border-emerald-400/20 bg-emerald-400/[0.03] p-12 text-center"
          data-testid="recovery-empty-all"
        >
          <CheckCircle weight="duotone" className="mx-auto h-10 w-10 text-emerald-300" />
          <div className="mt-4 font-display text-2xl tracking-tight text-emerald-100">
            Nothing needs attention right now
          </div>
          <p className="mt-2 text-sm text-ink-secondary max-w-md mx-auto">
            All open leads have been touched recently and every nurture step is on schedule.
          </p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-6">
          <FollowUpSection testid="recovery-due-today" title="Due today" rows={dueToday}
            empty="No follow-ups due today." />
          <FollowUpSection testid="recovery-overdue-followups" title="Overdue" rows={overdueFollowUps}
            empty="No overdue follow-ups." showOverdue />
          {/* ── Needs attention ────────────────────────────────────────── */}
          <section
            data-testid="recovery-needs-attention"
            className="rounded-md border border-white/10 bg-graphite-900"
          >
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
                  <Warning weight="duotone" className="h-3 w-3 text-amber-300/80" /> Open pipeline
                </div>
                <h2 className="mt-0.5 font-display text-lg tracking-tight">Needs attention</h2>
                <p className="mt-0.5 text-[11px] text-ink-secondary">
                  Open leads with no activity in {threshold}+ days.
                  <span className="text-amber-200/80"> Proxy on <code>updated_at</code> — not stage-transition history.</span>
                </p>
              </div>
              <div className="text-[11px] text-ink-secondary tabular-nums" data-testid="recovery-attention-count">
                {stale.length} lead{stale.length === 1 ? "" : "s"}
              </div>
            </div>
            {stale.length === 0 ? (
              <div className="p-8 text-center text-sm text-ink-secondary" data-testid="recovery-needs-attention-empty">
                Every open lead was touched in the last {threshold} days.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="recovery-needs-attention-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                      <th className="px-5 py-2.5 font-medium">Lead</th>
                      <th className="px-5 py-2.5 font-medium">Company</th>
                      <th className="px-5 py-2.5 font-medium">Stage</th>
                      <th className="px-5 py-2.5 font-medium text-right">No activity</th>
                      <th className="px-5 py-2.5 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {stale.map((l) => (
                      <tr key={l.lead_id} data-testid={`recovery-row-${l.lead_id}`} className="hover:bg-white/[0.02]">
                        <td className="px-5 py-2.5 text-ink-primary" data-testid={`recovery-name-${l.lead_id}`}>{l.name || "(unnamed)"}</td>
                        <td className="px-5 py-2.5 text-ink-secondary">{l.company || "—"}</td>
                        <td className="px-5 py-2.5 text-ink-secondary text-xs">
                          {l.stage_name ? (
                            <span className="inline-flex items-center rounded-full border border-white/10 bg-graphite-950 px-2 py-0.5">
                              {l.stage_name}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-5 py-2.5 text-right text-ink-secondary text-xs tabular-nums" data-testid={`recovery-days-${l.lead_id}`}>
                          <span className="inline-flex items-center gap-1">
                            <ClockCounterClockwise weight="duotone" className="h-3 w-3" />
                            {fmtDays(l.days_since_update)}
                          </span>
                        </td>
                        <td className="px-5 py-2.5 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              data-testid={`recovery-nudge-${l.lead_id}`}
                              onClick={() => openNudge(l)}
                              title="Draft a re-engagement email with the AI Assistant"
                              className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-ink-secondary hover:text-ink-primary hover:bg-white/5"
                            >
                              <ChatCircleDots weight="duotone" className="h-3 w-3" />
                              Draft email
                            </button>
                            {!l.in_active_sequence && (
                              <button
                                data-testid={`recovery-enroll-${l.lead_id}`}
                                onClick={() => openEnroll(l)}
                                className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-ink-secondary hover:text-ink-primary hover:bg-white/5"
                              >
                                <Plus weight="bold" className="h-3 w-3" />
                                Enroll
                              </button>
                            )}
                            {l.in_active_sequence && (
                              <span
                                data-testid={`recovery-in-sequence-${l.lead_id}`}
                                className="text-[10px] uppercase tracking-widest text-emerald-200/80 border border-emerald-400/25 bg-emerald-400/5 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1"
                              >
                                <FlowArrow weight="duotone" className="h-3 w-3" />
                                In sequence
                              </span>
                            )}
                            <Link
                              to="/leads"
                              data-testid={`recovery-view-${l.lead_id}`}
                              className="inline-flex items-center gap-1 rounded-full bg-white text-graphite-950 px-2.5 py-1 text-[11px] font-medium hover:bg-neutral-100"
                            >
                              View
                              <ArrowRight weight="bold" className="h-3 w-3" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── Nurture overdue ────────────────────────────────────────── */}
          <section
            data-testid="recovery-nurture-overdue"
            className="rounded-md border border-white/10 bg-graphite-900"
          >
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
                  <FlowArrow weight="duotone" className="h-3 w-3 text-fuchsia-300/80" /> Nurture
                </div>
                <h2 className="mt-0.5 font-display text-lg tracking-tight">Nurture overdue</h2>
                <p className="mt-0.5 text-[11px] text-ink-secondary">
                  Active enrollments whose current step is past its due date. Same data the
                  dashboard "Nurture: Due Today" widget uses.
                </p>
              </div>
              <div className="text-[11px] text-ink-secondary tabular-nums" data-testid="recovery-overdue-count">
                {overdue.length} enrollment{overdue.length === 1 ? "" : "s"}
              </div>
            </div>
            {overdue.length === 0 ? (
              <div className="p-8 text-center text-sm text-ink-secondary" data-testid="recovery-nurture-overdue-empty">
                No nurture steps are overdue right now.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="recovery-nurture-overdue-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                      <th className="px-5 py-2.5 font-medium">Lead</th>
                      <th className="px-5 py-2.5 font-medium">Sequence</th>
                      <th className="px-5 py-2.5 font-medium">Step</th>
                      <th className="px-5 py-2.5 font-medium text-right">Due date</th>
                      <th className="px-5 py-2.5 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {overdue.map((r) => (
                      <tr key={r.enrollment_id} data-testid={`recovery-overdue-row-${r.enrollment_id}`} className="hover:bg-white/[0.02]">
                        <td className="px-5 py-2.5 text-ink-primary">{r.lead_name || "(unnamed)"}</td>
                        <td className="px-5 py-2.5 text-ink-secondary text-xs">{r.sequence_name || "—"}</td>
                        <td className="px-5 py-2.5 text-ink-secondary text-xs">Step {r.current_step_order + 1}</td>
                        <td className="px-5 py-2.5 text-right text-amber-200/90 text-xs tabular-nums" data-testid={`recovery-overdue-due-${r.enrollment_id}`}>
                          {r.current_step_due_date || "—"}
                        </td>
                        <td className="px-5 py-2.5 text-right">
                          <Link
                            to={`/nurture-sequences/${r.sequence_id}`}
                            data-testid={`recovery-overdue-open-${r.enrollment_id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-white text-graphite-950 px-2.5 py-1 text-[11px] font-medium hover:bg-neutral-100"
                          >
                            Open sequence
                            <ArrowRight weight="bold" className="h-3 w-3" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {/* Enroll dialog */}
      <Dialog open={!!enrollLead} onOpenChange={(v) => { if (!v) { setEnrollLead(null); setChosenSeqId(""); } }}>
        <DialogContent data-testid="recovery-enroll-dialog" className="max-w-lg bg-graphite-900 border-white/10 text-ink-primary">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight flex items-center gap-2">
              <FlowArrow weight="duotone" className="h-5 w-5 text-ink-secondary" />
              Enroll in sequence
            </DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Enroll <span className="text-ink-primary">{enrollLead?.name}</span> in an active nurture sequence.
            </DialogDescription>
          </DialogHeader>
          {sequences.length === 0 ? (
            <div className="text-sm text-ink-secondary">
              No active sequences yet. Create one in <Link to="/nurture-sequences" className="text-ink-primary underline">Nurture Sequences</Link>.
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Sequence</label>
              <select
                data-testid="recovery-enroll-select"
                value={chosenSeqId}
                onChange={(e) => setChosenSeqId(e.target.value)}
                className="w-full h-10 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30"
              >
                <option value="">— Choose a sequence —</option>
                {sequences.map((s) => (
                  <option key={s.sequence_id} value={s.sequence_id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button
              data-testid="recovery-enroll-cancel"
              onClick={() => { if (!enrolling) { setEnrollLead(null); setChosenSeqId(""); } }}
              disabled={enrolling}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              data-testid="recovery-enroll-confirm"
              onClick={doEnroll}
              disabled={enrolling || !chosenSeqId}
              className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60"
            >
              {enrolling ? "Enrolling…" : "Enroll"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
